/**
 * Release deep links (single source for notifications, dashboard cards and the Portfolio inbox).
 *
 * - In a brand: `/approvals?postId=&variationId=` opens the Release drawer on the board.
 * - In the Brand Portfolio: `/agency/portfolio?openBrand=&panel=approvals&postId=&variationId=`
 *   enters the brand (God Mode) and then opens the same drawer; AgencyPortfolio handles it.
 */

import { RELEASE_BOARD_COLUMN_KEYS, type ReleaseBoardColumnKey } from '@/types/releaseBoard';

export type ReleaseDrawerTab = 'comments' | 'details' | 'history';

export interface ReleaseLinkTarget {
  postId?: number | null;
  variationId?: number | null;
  /** Drawer tab to open */
  tab?: ReleaseDrawerTab;
  /** Board column to scroll to and focus (`?column=`); unknown values are never written */
  column?: ReleaseBoardColumnKey | null;
}

function appendTarget(params: URLSearchParams, target: ReleaseLinkTarget): void {
  if (target.postId) {
    params.set('postId', String(target.postId));
    if (target.variationId) params.set('variationId', String(target.variationId));
  }
  if (target.tab) params.set('tab', target.tab);
  if (target.column && RELEASE_BOARD_COLUMN_KEYS.includes(target.column)) params.set('column', target.column);
}

/** Board link inside the current brand. Without a post it is the plain board. */
export function buildBoardReleaseHref(target: ReleaseLinkTarget = {}): string {
  const params = new URLSearchParams();
  appendTarget(params, target);
  const query = params.toString();
  return query ? `/approvals?${query}` : '/approvals';
}

export interface PortfolioReleaseLinkTarget extends ReleaseLinkTarget {
  teamId: number;
  /** Land on the brand's Release Board without opening a drawer (bundled notifications) */
  openBoard?: boolean;
}

/** Brand Portfolio link that enters the brand and opens the release drawer for the item. */
export function buildPortfolioReleaseHref(target: PortfolioReleaseLinkTarget): string {
  const params = new URLSearchParams({ openBrand: String(target.teamId), panel: 'approvals' });
  const { postId, variationId, tab, column } = target;
  appendTarget(params, { postId, variationId, column });
  if (target.openBoard) params.set('action', 'open_board');
  if (tab) params.set('tab', tab);
  return `/agency/portfolio?${params.toString()}`;
}

/**
 * Variation editor deep link: the Variations page reads `?post=<postId>&variation=<variationId>` and opens the
 * editor on exactly that version. The editor applies the release lock itself (In review / Released = read-only
 * with the lock banner), so the link is safe to follow from any state.
 */
export function buildVariationEditorHref(postId: number, variationId: number): string {
  return `/variations?${new URLSearchParams({ post: String(postId), variation: String(variationId) }).toString()}`;
}
